import { useState, useEffect } from 'react'
import type { DebugQuery } from '../../types/dashboard'
import { highlightSql, buildRunnableSql } from '../../lib/dashboardHelpers'

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)
    const handleCopy = async () => {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }
    return (
        <button onClick={handleCopy} style={{
            background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 500,
            color: copied ? '#10b981' : '#94a3b8', cursor: 'pointer', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: 4
        }}>
            {copied ? '✓ Copied' : '📋 Copy'}
        </button>
    )
}

export default function SqlDebugModal({ queries, activeProfile, connectionInfo, onClose }: {
    queries: DebugQuery[]; activeProfile?: string; connectionInfo?: string; onClose: () => void
}) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px', width: '100%', maxWidth: 960,
                maxHeight: '85vh', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>🐛</span>
                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#e2e8f0' }}>
                            SQL Debug — {queries.length} queries executed
                        </h2>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {queries.length > 0 && (
                            <CopyButton text={queries.map((q, i) => `-- ${q.label}\n${buildRunnableSql(q, i)}`).join('\n\n')} />
                        )}
                        <button onClick={onClose} style={{
                            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 8, width: 32, height: 32, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            color: '#94a3b8', fontSize: 16, cursor: 'pointer', transition: 'all 0.2s'
                        }} onMouseOver={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.2)')}
                            onMouseOut={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}>✕</button>
                    </div>
                </div>

                {/* Connection info banner */}
                {(activeProfile || connectionInfo) && (
                    <div style={{
                        padding: '10px 24px', background: 'rgba(139, 92, 246, 0.1)',
                        borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
                        display: 'flex', flexWrap: 'wrap', gap: '16px',
                        fontSize: 13, color: '#c4b5fd'
                    }}>
                        {activeProfile && (
                            <span>🔌 <strong>Profile:</strong> {activeProfile}</span>
                        )}
                        {connectionInfo && (
                            <span>🗄️ <strong>Legacy DB:</strong> {connectionInfo}</span>
                        )}
                    </div>
                )}

                <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
                    {queries.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
                            No queries executed yet. Load the dashboard first.
                        </div>
                    )}
                    {queries.map((q, i) => {
                        const runnableSql = buildRunnableSql(q, i)
                        return (
                            <div key={i} style={{
                                marginBottom: 20, background: 'rgba(255,255,255,0.03)',
                                borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden'
                            }}>
                                <div style={{
                                    padding: '8px 14px', background: 'rgba(167,139,250,0.08)',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    fontSize: 12, fontWeight: 600, color: '#a78bfa',
                                    display: 'flex', alignItems: 'center', gap: 8
                                }}>
                                    <span style={{ background: 'rgba(167,139,250,0.2)', borderRadius: 4, padding: '1px 6px', fontSize: 10, color: '#c4b5fd' }}>Q{i + 1}</span>
                                    <span style={{ flex: 1 }}>{q.label}</span>
                                    <CopyButton text={runnableSql} />
                                </div>
                                <pre style={{
                                    margin: 0, padding: '14px 14px', fontSize: 12.5, lineHeight: 1.6,
                                    fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                                    color: '#cbd5e1', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                                }} dangerouslySetInnerHTML={{ __html: highlightSql(runnableSql) }} />
                            </div>
                        )
                    })}
                </div>
                <div style={{
                    padding: '12px 24px', borderTop: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex', justifyContent: 'flex-end', gap: 8
                }}>
                    <button onClick={onClose} style={{
                        padding: '8px 20px', borderRadius: 8,
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                        color: '#e2e8f0', fontSize: 13, fontWeight: 500,
                        cursor: 'pointer', transition: 'all 0.2s'
                    }}>Close</button>
                </div>
            </div>
        </div>
    )
}
